import { t } from '@lingui/macro'
import { Flex, FormControl, Select } from 'front-end/src/components'
import IconRefresh from 'front-end/src/components/common/icon/IconRefresh'
import React, { useState } from 'react'
import { FormLabelIcon } from '../../../components/event-form/EventFormLabelIcon'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { EventRecurrenceModal } from './EventRecurrenceModal'
import { useFormikContext } from 'formik'
import {
  EventRecurrencePattern,
  RecurringEventAttendanceTypeEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { ManageEventDetailDayjs } from '../../EventEditPage'
import { useManageEventContext } from '../../../ManageEventContext'
import { get, isEqual } from 'lodash'
import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import { ReactSelectOption } from 'front-end/src/components/common/select/Select'
import dayjs from 'shared/dayjs'

export enum EventRepeatOptsEnum {
  NEVER_REPEAT = 'NEVER_REPEAT',
  REPEAT_PATTERN = 'REPEAT_PATTERN',
  CREATE_CUSTOM = 'CREATE_CUSTOM'
}

export const EventDetailRepeat = () => {
  const { event, isRecurringSeries } = useManageEventContext()
  const accessor = isRecurringSeries
    ? 'recurrence'
    : 'recurringEvent.recurrence'
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<ManageEventDetailDayjs>()

  const getRepeatOptions = (recurrence = get(values, accessor)) => {
    const repeatOptions: ReactSelectOption[] = []

    if (!event.published_date) {
      repeatOptions.push({
        value: EventRepeatOptsEnum.NEVER_REPEAT,
        label: t`Never repeat`
      })
    }
    if (!recurrence) {
      repeatOptions.push({
        value: EventRepeatOptsEnum.CREATE_CUSTOM,
        label: t`Create custom recurrence...`
      })
    } else {
      repeatOptions.push(
        {
          label: <RecurrenceText recurrence={recurrence} />,
          value: EventRepeatOptsEnum.REPEAT_PATTERN
        },
        {
          value: EventRepeatOptsEnum.CREATE_CUSTOM,
          label: t`Edit custom recurrence...`
        }
      )
    }
    return repeatOptions
  }

  const [repeatOptions, setRepeatOptions] = useState<ReactSelectOption[]>(
    getRepeatOptions()
  )
  const [recurrence, setRecurrence] = useState(get(values, accessor) || null)
  const [selectedRepeatOption, setSelectedRepeatOption] = useState(
    !event.published_date && recurrence ? repeatOptions[1] : repeatOptions[0]
  )
  const { open, currentModal: recurrenceModal } = useModal()

  const setFormFieldValue = (
    recurrenceValues: EventRecurrencePattern | null = null
  ) => {
    if (!recurrenceValues) {
      setFieldValue(`${accessor}.freq`, null)
      setFieldValue(`${accessor}.interval`, null)
      setFieldValue(`${accessor}.byDay`, null)
      setFieldValue(`${accessor}.count`, null)
    }
    setFieldValue(`${accessor}.freq`, recurrenceValues?.freq)
    setFieldValue(`${accessor}.interval`, recurrenceValues?.interval)
    setFieldValue(`${accessor}.byDay`, recurrenceValues?.byDay)
    setFieldValue(`${accessor}.count`, recurrenceValues?.count)
    setFieldTouched(accessor, !isEqual(event[accessor], recurrenceValues))
  }

  const onRepeatChange = (value) => {
    setSelectedRepeatOption(value)
    if (value.value === EventRepeatOptsEnum.CREATE_CUSTOM) {
      open(EventRecurrenceModal, { eventFormValues: values }).then(
        (recurrenceValues: EventRecurrencePattern) => {
          setRecurrence(recurrenceValues)
          setFormFieldValue(recurrenceValues)
          const options = getRepeatOptions(recurrenceValues)
          setRepeatOptions(options)
          const repeatOption = options.find(
            (opt) => opt.value === EventRepeatOptsEnum.REPEAT_PATTERN
          )
          if (repeatOption) {
            setSelectedRepeatOption(repeatOption)
          }
          const defaultStartTime = dayjs().startOf('hour')
          const defaultEndTime = dayjs().startOf('hour').add(1, 'hour')

          if (values.start_time.isBefore(defaultStartTime)) {
            setFieldValue('start_time', defaultStartTime)
          }

          if (values.end_time.isBefore(defaultEndTime)) {
            setFieldValue('end_time', defaultEndTime)
          }

          if (!values.attendance_type) {
            setFieldValue(
              'attendance_type',
              RecurringEventAttendanceTypeEnum.all
            )
          }
        }
      )
    }
    if (value.value === EventRepeatOptsEnum.REPEAT_PATTERN) {
      setFormFieldValue(recurrence)
    }
    if (value.value === EventRepeatOptsEnum.NEVER_REPEAT) {
      setFormFieldValue()
    }
  }

  return (
    <FormControl as={Flex} mb="32px" flexGrow={1} id="recurrence">
      <Flex shrink={1}>
        <FormLabelIcon icon={<IconRefresh aria-label="Recurrence" />} />
      </Flex>
      <Flex>
        <Select
          key={`${selectedRepeatOption.label}`}
          options={repeatOptions}
          onChange={onRepeatChange}
          value={selectedRepeatOption}
          styles={{
            container: (base) => ({
              ...base,
              minWidth: 282
            })
          }}
          dataTest="RecurrenceSelect"
        />
      </Flex>
      {recurrenceModal}
    </FormControl>
  )
}
